import { cn } from "@/lib/utils";
import { isEmpty } from "lodash";
import React, {
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useMemo,
} from "react";
import { useRef, useState } from "react";
import type { ZodError, ZodSchema } from "zod";

/**
 * Custom validation component to be able to use Zod for validation to values
 *
 * @param props - form configuration and validation parameters.
 *
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <ValidationRoot schema={zodSchema} values={values}>
 *       <input defaultValue="test" {...register("example")} />
 *        <ValidationErrorMessage name="example" />
 *     </ValidationRoot>
 *   );
 * }
 * ```
 */

type ValidationRootProps = {
  schema: ZodSchema;
  values: any;
  validateOnDirty?: boolean;
  children?:
    | React.ReactNode
    | ((props: { isValid: boolean }) => React.ReactNode);
};

export const ValidationContext = React.createContext<{
  errors: ZodError["issues"];
}>({
  errors: [],
});

const ValidationRoot = ({
  schema,
  values,
  children,
  validateOnDirty = false,
}: ValidationRootProps) => {
  const getInitialErrors = () => {
    if (validateOnDirty) {
      return [];
    }

    if (isEmpty(values)) {
      return [];
    }

    try {
      const parsedSchema = schema.safeParse(values);
      if (!parsedSchema.success) {
        return parsedSchema.error.issues;
      }
      return [];
    } catch (_error) {
      const error = _error as ZodError;
      return error.issues;
    }
  };

  const [errors, setErrors] = useState<ZodError["issues"]>(getInitialErrors);
  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef(values);

  useEffect(() => {
    if (!isDirty && values !== initialValuesRef.current) {
      setIsDirty(true);
    }
  }, [values, isDirty]);

  useEffect(() => {
    if (!isDirty && validateOnDirty) {
      return;
    }

    try {
      if (isEmpty(values)) {
        setErrors([]);
        return;
      }

      const parsedSchema = schema.safeParse(values);

      if (!parsedSchema.success) {
        throw parsedSchema.error;
      }

      setErrors([]);
    } catch (_error) {
      const error = _error as ZodError;
      setErrors(error.issues);
    }
  }, [values, schema]);

  return (
    <ValidationContext.Provider value={{ errors }}>
      {typeof children === "function"
        ? children({ isValid: errors.length === 0 })
        : children}
    </ValidationContext.Provider>
  );
};

type ValidationErrorsProps = {
  name: string;
  children?: ({ error }: { error?: string }) => ReactElement;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
};

const ValidationErrorMessage = ({
  name,
  className,
  children,
}: ValidationErrorsProps) => {
  const context = useValidation();

  if (context === undefined) {
    throw new Error(
      "useAccountsContext must be used within a AccountsDataProvider",
    );
  }

  const error = useMemo(() => {
    const error = context.errors.find((error) => error.path.join(".") === name);
    return error?.message;
  }, [context, name]);

  if (children) {
    return <>{children({ error })}</>;
  }

  if (!error) {
    return null;
  }

  return (
    <p
      id={`${name}-error`}
      className={cn("text-[0.8rem] font-medium text-destructive", className)}
    >
      {error}
    </p>
  );
};

type ValidationErrorBorderProps = PropsWithChildren<{
  name?: string;
  className?: string;
}>;

const ValidationErrorBorder = ({
  name,
  children,
  className,
}: ValidationErrorBorderProps) => {
  const context = useValidation();

  const hasError = name
    ? context.errors.some((error) => error.path.join(".") === name)
    : context.errors.length > 0;

  return (
    <div
      className={cn(
        "relative w-full",
        hasError && "[&>*:first-child]:relative [&>*:first-child]:z-[1]",
        hasError &&
          "before:absolute before:inset-[-2px] before:rounded-md before:ring-2 before:ring-destructive",
        hasError && className,
      )}
    >
      {children}
    </div>
  );
};

const useValidation = () => {
  const context = React.useContext(ValidationContext);

  if (context === undefined) {
    throw new Error(
      "useValidation must be used within a ValidationRoot component",
    );
  }

  return {
    isValid: context.errors.length === 0,
    errors: context.errors,
  };
};

export {
  ValidationRoot,
  ValidationErrorMessage,
  ValidationErrorBorder,
  useValidation,
};
